/**
 * Chat input bar with send button.
 */

import React, { useRef, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import {
  TemporaryAttachmentPicker,
  type TemporaryAttachmentItem,
} from '@/components/common/TemporaryAttachmentPicker';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { hasAttachmentErrors } from '@/lib/temporaryAttachments';

interface Props {
  onSend: (message: string, attachments?: File[]) => Promise<boolean> | boolean | void;
  disabled?: boolean;
  placeholder?: string;
  attachments: TemporaryAttachmentItem[];
  onAttachmentsChange: (items: TemporaryAttachmentItem[]) => void;
  attachmentsProcessing: boolean;
  onAttachmentsProcessingChange: (value: boolean) => void;
}

const ChatInput: React.FC<Props> = ({
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  attachments,
  onAttachmentsChange,
  attachmentsProcessing,
  onAttachmentsProcessingChange,
}) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = async () => {
    const trimmed = value.trim();
    const files = attachments.map((item) => item.file);
    if ((!trimmed && files.length === 0) || disabled || attachmentsProcessing || hasAttachmentErrors(attachments)) return;
    const previousAttachments = attachments;
    setValue('');
    onAttachmentsChange([]);
    onAttachmentsProcessingChange(false);
    const sent = await onSend(trimmed, files);
    if (sent === false) {
      onAttachmentsChange(previousAttachments);
    }
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // Auto-resize textarea
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card p-2 shadow-sm">
      <TemporaryAttachmentPicker.Chips />
      <div className="flex items-end gap-2">
        <TemporaryAttachmentPicker.Trigger />
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || attachmentsProcessing}
          rows={1}
          className="min-h-[36px] max-h-[120px] resize-none border-0 bg-transparent p-1.5 text-sm shadow-none focus-visible:ring-0"
        />

        <Button
          size="icon"
          variant="ghost"
          onClick={() => void handleSend()}
          disabled={
            disabled
            || attachmentsProcessing
            || (!value.trim() && attachments.length === 0)
            || hasAttachmentErrors(attachments)
          }
          className="h-9 w-9 shrink-0 rounded-lg text-primary hover:bg-primary/10"
        >
          {disabled || attachmentsProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default ChatInput;
